import { useEffect, useMemo, useState } from 'react';

import { Card } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import Layout from 'components/Layout/Layout';

import RouteReportBlockedState, {
  RouteReportEmptyDay,
  RouteReportErrorState,
} from './components/RouteReportBlockedState';
import RouteReportFilters from './components/RouteReportFilters';
import RouteReportIncidences from './components/RouteReportIncidences';
import RouteReportIncidencesSkeleton from './components/RouteReportIncidencesSkeleton';
import RouteReportKpis from './components/RouteReportKpis';
import RouteReportProgress from './components/RouteReportProgress';
import RouteTimeline from './components/RouteTimeline';
import RouteTimelineSkeleton from './components/RouteTimelineSkeleton';
import RoutesTable from './components/RoutesTable';
import { useRouteReport } from './hooks/useRouteReport';
import { useRouteReportScope } from './hooks/useRouteReportScope';
import { useRouteReportTheme } from './hooks/useRouteReportTheme';
import { installRouteReportSimulator } from './utils/simulator';
import { getUserTimeZone } from './utils/time';

import './RouteReport.scss';

function RouteReport() {
  const scope = useRouteReportScope();
  // El día arranca en hoy según el reloj del usuario. El endpoint no tiene
  // default a propósito, así que la fecha siempre viaja explícita.
  const [date, setDate] = useState<Dayjs>(dayjs());

  const dateParam = date.format('YYYY-MM-DD');
  const {
    loading: reportLoading,
    refreshing,
    report,
    lastUpdatedAt,
    error: reportError,
    refresh,
  } = useRouteReport({
    // No se consulta hasta saber que el usuario alcanza algún distribuidor.
    date: scope.loading || scope.blockedReason ? undefined : dateParam,
    distributorId: scope.selectedDistributorId,
  });

  const userTimeZone = useMemo(() => getUserTimeZone(), []);

  /**
   * Deja `simularRutas()` disponible en la consola mientras esta pantalla está
   * abierta, para demostrar el avance en vivo sin depender de que haya
   * movimiento real en terreno. Se limpia al salir de la vista.
   */
  useEffect(
    () =>
      installRouteReportSimulator({
        date: dateParam,
        distributorId: scope.selectedDistributorId,
      }),
    [dateParam, scope.selectedDistributorId]
  );

  /**
   * Las variables de marca del módulo. Se publican acá para todo lo que vive
   * DENTRO de la página; los modales las publican por su cuenta porque antd los
   * portea a `document.body` y esta cascada no llega hasta ellos.
   */
  const themeVars = useRouteReportTheme();

  if (scope.loading) {
    return (
      <Layout title="">
        <Card loading />
      </Layout>
    );
  }

  if (scope.blockedReason) {
    return (
      <Layout title="">
        <RouteReportBlockedState reason={scope.blockedReason} />
      </Layout>
    );
  }

  const routes = report?.routes ?? [];
  // Un día sin plan responde 200 con la lista vacía: es un vacío, no un error.
  const emptyDay = !reportLoading && report !== null && routes.length === 0;

  return (
    <Layout title="">
      <div className="route-report" style={themeVars}>
        <RouteReportFilters
          date={date}
          onDateChange={setDate}
          distributors={scope.distributors}
          selectedDistributorId={scope.selectedDistributorId}
          onDistributorChange={scope.setSelectedDistributorId}
          singleDistributor={scope.singleDistributor}
          loading={reportLoading}
          routeCount={routes.length}
          refreshing={refreshing}
          onRefresh={refresh}
          lastUpdatedAt={lastUpdatedAt}
          userTimeZone={userTimeZone}
        />

        {reportError ? (
          // Sin esto la pantalla se quedaba con los skeletons puestos: `report`
          // en `null` y `loading` en `false` se ven igual que "todavía cargando".
          <RouteReportErrorState onRetry={refresh} />
        ) : emptyDay ? (
          <RouteReportEmptyDay context={report?.context ?? null} />
        ) : (
          <>
            <RouteReportKpis
              kpis={report?.kpis ?? null}
              loading={reportLoading}
            />

            <RouteReportProgress
              progress={report?.progress ?? null}
              context={report?.context ?? null}
              userTimeZone={userTimeZone}
              loading={reportLoading}
            />

            {/* Timeline + incidencias comparten wrapper de dos columnas
                (T3). `RoutesTable` queda AFUERA a propósito: así, cuando el
                wrapper colapsa a una columna, la card de incidencias cae
                sola entre "Camionetas en ruta" y "Rutas del día", que es el
                orden pedido, sin necesitar ningún `order` en CSS. */}
            <div className="route-report-columns">
              {/* El timeline necesita `context` para escalar el eje, así que
                  no puede renderizarse a medias: mientras no haya datos va su
                  propio bloque de carga, con las mismas medidas. */}
              {reportLoading || !report ? (
                <RouteTimelineSkeleton />
              ) : (
                <RouteTimeline
                  routes={routes}
                  context={report.context}
                  userTimeZone={userTimeZone}
                />
              )}

              {reportLoading || !report ? (
                <RouteReportIncidencesSkeleton />
              ) : (
                <RouteReportIncidences
                  routes={routes}
                  userTimeZone={userTimeZone}
                  successfulStops={report.kpis.successfulStops}
                  scheduledStops={report.kpis.scheduledStops}
                />
              )}
            </div>

            <RoutesTable
              routes={routes}
              excludedRoutes={report?.excludedRoutes ?? 0}
              userTimeZone={userTimeZone}
              loading={reportLoading}
            />
          </>
        )}
      </div>
    </Layout>
  );
}

export default RouteReport;
